import { SelectChangeEvent } from '@mui/material/Select';
import { AxiosError } from 'axios';
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useSelector } from 'react-redux';
import {
  IPolicyRepresentation,
  PolicyQuery,
} from '@bom-nextgen-keycloak/models';
import {
  CONFIG,
  selectPermissionPolicy,
  selectPermissionPolicyDetail,
  useAlertMessage,
  useNotification,
  useResource,
} from '@bom-nextgen-keycloak/web/core';
import {
  deletePolicy,
  ErrorMessage,
  fetchPolicy,
  QUERY_KEY,
} from '@bom-nextgen-keycloak/web/shared/data-access';
import {
  SearchboxEmit,
  SearchboxOptions,
} from '@bom-nextgen-keycloak/web/shared/ui';
import {
  checkProcessing,
  generateHash,
  useDebounce,
} from '@bom-nextgen-keycloak/web/shared/util';
import { AdvancedSearchCriteria } from '../components';

type Arguments = {
  callback?: () => void;
  disableQuery?: boolean;
};

type PolicyType = string;

const usePolicy2 = (options?: Arguments) => {
  const callback = options?.callback;
  const disableQuery = options?.disableQuery;
  const enableQuery = !disableQuery;
  const resource = useResource();
  const { clientId } = resource;
  const defaultParams = {
    first: CONFIG.pagination.offset,
    max: CONFIG.pagination.step,
    name: '',
    type: 'all',
  };
  const defaultData = {
    items: [],
    totalCount: undefined,
    totalRecord: 0,
  };
  const defaultSelection = {
    item: null,
    type: '',
  };
  const queryClient = useQueryClient();
  const { setAlertMessage } = useAlertMessage();
  const { setNotification } = useNotification();
  const { canViewAdd: canAdd, canViewDelete: canDelete } = useSelector(
    selectPermissionPolicy
  );
  const { canViewPage: canEdit } = useSelector(selectPermissionPolicyDetail);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [params, setParams] = useState<PolicyQuery>({
    ...defaultParams,
  });
  const [selectedType, setSelectedType] = useState<PolicyType>(
    defaultSelection.type
  );
  const [selectedItem, setSelectedItem] =
    useState<IPolicyRepresentation | null>(defaultSelection.item);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentOffset, setCurrentOffset] = useState(CONFIG.pagination.offset);
  const [previousOffset, setPreviousOffset] = useState(
    CONFIG.pagination.offset
  );
  const [pageStep, setPageStep] = useState(CONFIG.pagination.step);
  const [queryHash, setQueryHash] = useState(generateHash());
  const [isSearching, setIsSearching] = useState(false);
  const [wasSearch, setWasSearch] = useState(false);
  const debouncedSearchQuery = useDebounce(
    searchQuery,
    CONFIG.ui.debounceDelay
  );

  const isSearch = searchQuery !== '';
  const isFilter = params.type !== 'all';
  const queryKeys = [
    QUERY_KEY.POLICY_LIST,
    clientId,
    queryHash,
    debouncedSearchQuery,
  ];

  const {
    data = defaultData,
    isFetched,
    isFetching,
    isLoading,
  } = useQuery(
    queryKeys,
    () =>
      fetchPolicy(clientId, {
        ...params,
        type: params.type === 'all' ? '' : params.type,
      }),
    {
      enabled: enableQuery,
      onError: (error: AxiosError<ErrorMessage>) => {
        const message = error.response?.data.message || 'Cannot get policies';

        setAlertMessage({
          message,
          statusCode: error.response?.status,
          typeStatusMessage: 'error',
        });
      },
      onSuccess: () => {
        if ((isSearch && isSearching) || (isFilter && isSearching)) {
          setIsSearching(false);
        }

        if (wasSearch && !isSearch && !isFilter) {
          setWasSearch(false);
        }

        if (!isSearch && !isFilter) {
          setPreviousOffset(params.first);
        }
      },
      staleTime: CONFIG.query.staleTime,
    }
  );

  const isProcessing = checkProcessing({ isFetched, isFetching, isLoading });

  const deleteMutation = useMutation(deletePolicy, {
    onError: (error: AxiosError<ErrorMessage>) => {
      const message = error.response?.data.message || 'Cannot delete policy';

      setAlertMessage({
        message,
        statusCode: error.response?.status,
        typeStatusMessage: 'error',
      });
    },
    onSuccess: () => {
      setIsDeleteDialogOpen(false);
      setNotification({
        message: 'Policy deleted',
        type: 'success',
      });

      if (enableQuery) {
        handleSelectionReset();
        handleReload();
      }

      callback && callback();
    },
  });

  const handleSelectionReset = () => {
    setSelectedItem(defaultSelection.item);
    setSelectedType(defaultSelection.type);
  };

  const handleRefetch = () => {
    setQueryHash(generateHash());
  };

  const handleReload = () => {
    queryClient.invalidateQueries(queryKeys);
  };

  const handleRefresh = () => {
    const offset = 0;

    setCurrentOffset(offset);
    setParams((previous) => ({
      ...previous,
      first: offset,
    }));
    handleRefetch();
  };

  const handleCreateRequest = (type: PolicyType) => {
    setSelectedType(type);
    setIsCreateDialogOpen(true);
  };

  const handleDeleteRequest = (data: IPolicyRepresentation) => {
    if (data) {
      setSelectedItem(data);
      setIsDeleteDialogOpen(true);
    } else {
      // TODO: Implement a handler
    }
  };

  const handleCreateCancel = () => {
    setIsCreateDialogOpen(false);
    handleSelectionReset();
  };

  const handleDeleteCancel = () => {
    setIsDeleteDialogOpen(false);
    handleSelectionReset();
  };

  const handleCreateAction = () => {
    setIsCreateDialogOpen(false);
    handleSelectionReset();
    handleReload();
  };

  const handleDeleteAction = () => {
    if (selectedItem) {
      deleteMutation.mutate({
        clientId,
        policyId: selectedItem.id,
      });
    } else {
      // TODO: Implement a handler
    }
  };

  const handleSearch = ({ event, isEnter = false }: SearchboxOptions) => {
    const { value } = event;
    const keyword = value.trim();
    const hasKeyword = keyword !== '';
    const hasChanged = keyword !== '' && keyword !== searchQuery;
    const offset = hasKeyword ? 0 : previousOffset;

    setCurrentOffset(offset);
    setIsSearching(hasChanged || isFilter);
    setParams((previous) => ({
      ...previous,
      first: offset,
      name: value,
    }));

    if (isEnter && hasKeyword) {
      handleRefetch();
    } else {
      setSearchQuery((previous) => {
        setWasSearch(previous !== '' && !hasKeyword && !isFilter);

        return value;
      });
    }
  };

  const handleSearchChange = (event: SearchboxEmit) => {
    handleSearch({ event, isEnter: false });
  };

  const handleSearchEnter = (event: SearchboxEmit) => {
    handleSearch({ event, isEnter: true });
  };

  const handleSearchReset = () => {
    const value = '';
    const hasKeyword = isSearch;
    const offset = previousOffset;

    setCurrentOffset(offset);
    setParams((previous) => ({
      ...defaultParams,
      first: offset,
      max: previous.max,
    }));

    if (hasKeyword) {
      setWasSearch(true);
      setSearchQuery(value);
    } else {
      handleRefetch();
    }
  };

  const handleSearchClear = () => {
    const value = '';
    const hasKeyword = isSearch;

    if (hasKeyword) {
      if (isFilter) {
        setIsSearching(true);
        setWasSearch(false);
        setParams((previous) => ({
          ...previous,
          name: value,
        }));
        setSearchQuery(value);
      } else {
        handleSearchReset();
      }
    } else {
      // NOTE: Clear whitespaces without making an API request
      setSearchQuery(value);
    }
  };

  const handleAdvancedSearchChange = (criteria: AdvancedSearchCriteria) => {
    const { type } = criteria;
    const hasCriteria = type !== 'all';
    const offset = hasCriteria ? 0 : previousOffset;

    setIsSearching(hasCriteria);
    setCurrentOffset(offset);
    setParams((previous) => ({
      ...previous,
      ...criteria,
      first: offset,
    }));
    handleRefetch();
  };

  const handlePageChange = (offset: number) => {
    if (wasSearch) {
      setWasSearch(false);
    }

    setCurrentOffset(offset);
    setParams((previous) => ({
      ...previous,
      first: offset,
    }));
    handleRefetch();
  };

  const handleStepChange = (event: SelectChangeEvent<unknown>) => {
    const step = event.target.value as number;
    const offset = 0;

    setCurrentOffset(offset);
    setPreviousOffset(offset);
    setPageStep(step);
    setParams((previous) => ({
      ...previous,
      first: offset,
      max: step,
    }));
    handleRefetch();
  };

  return {
    canAdd,
    canDelete,
    canEdit,
    data,
    handleAdvancedSearchChange,
    handleCreateAction,
    handleCreateCancel,
    handleCreateRequest,
    handleDeleteAction,
    handleDeleteCancel,
    handleDeleteRequest,
    handlePageChange,
    handleRefresh,
    handleSearchChange,
    handleSearchClear,
    handleSearchEnter,
    handleSearchReset,
    handleStepChange,
    isCreateDialogOpen,
    isDeleteDialogOpen,
    isDeleting: deleteMutation.isLoading,
    isFetched,
    isFetching,
    isFilter,
    isLoading,
    isProcessing,
    isSearch,
    isSearching,
    paginationOffset: currentOffset,
    paginationStep: pageStep,
    params,
    searchQuery,
    selectedItem,
    selectedType,
    wasSearch,
  };
};

export { usePolicy2 };
